import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './app';
import { AuthProvider } from './lib/auth';
import { ApiError, shouldRetryApiQuery } from './lib/api-client';
import { Sentry } from './lib/sentry'; // Initialize Sentry before rendering (idempotent if DSN not set)
// Load Geist font via JS import so Vite picks up the package's `files/*.woff2`
// assets through its resolver. Importing via Tailwind's `@import` in
// styles.css is brittle on CI (`--ignore-scripts` + isolated pnpm layout)
// and emits raw `./files/...` URLs that 404 at runtime as the SPA fallback.
import '@fontsource-variable/geist';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: shouldRetryApiQuery,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!(error instanceof ApiError)) return;
      Sentry.addBreadcrumb({
        category: 'query-error',
        message: 'query-error',
        level: error.status === 429 ? 'warning' : 'info',
        data: {
          queryKey: JSON.stringify(query.queryKey),
          status: error.status,
          path: error.path,
          retryAfter: error.retryAfter,
        },
      });
    },
  }),
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
